import * as E from 'redux-saga/effects'
import {
  PayloadAction,
  createAction,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit'

import * as utils from '../utils'

import * as api from '../api'
import * as common from './common'
import { selectSessionVal, setSessionVal } from './session'

const PREFIX = 'env'

const initialState: utils.DBValues = utils.emptyObj

const envSlice = createSlice({
  initialState,
  name: PREFIX,
  reducers: {
    /**
     * Object.assign(oldEnv, newEnv).
     */
    mergeEnv(
      env,
      { payload: { newEnv } }: PayloadAction<{ newEnv: utils.DBValues }>,
    ) {
      Object.assign(env, newEnv)
    },
    setEnv(
      env,
      {
        payload: { key, value },
      }: PayloadAction<{ key: string; value: utils.DBVal }>,
    ) {
      // Clash between immer and Readonly<>
      // @ts-expect-error
      env[key] = value
    },
  },
})

export const {
  actions: { mergeEnv, setEnv },
  reducer: env,
} = envSlice

export const subToEnv = createAction(`${PREFIX}/subToEnv`)

export const unSubFromEnv = createAction(`${PREFIX}/unSubFromEnv`)

export const writeEnvKey = createAction<{ key: string; value: utils.DBVal }>(
  `${PREFIX}/writeEnvKey`,
)

export type WriteEnvKeyResult = { readonly ok: boolean; readonly err?: string }
export const writeEnvKeyResult = createAction<WriteEnvKeyResult>(
  `${PREFIX}/writeEnvKeyResult`,
)

// #region selectors

type GlobalState = utils.DeepReadonly<{
  [PREFIX]: utils.DBValues
}>

export const selectAllEnv = ({ env }: GlobalState): utils.DBValues => {
  if (Object.keys(env).length > 0) {
    return env
  }
  return utils.EMPTY_OBJ
}

export type SelectEnvParams = utils.DeepReadonly<{
  key: string
}>

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const makeSelectEnv = () =>
  createSelector(
    selectAllEnv,
    (_: GlobalState, args: SelectEnvParams) => args,
    (env, { key }): utils.DBVal => env[key],
  )

export const selectEnv =
  <Cast = utils.DBVal>(key: string) =>
  (state: GlobalState): Cast | undefined =>
    state[PREFIX][key] as Cast | undefined

// #endregion selectors

// #region sagas
const createEnvConn = (emit: (newEnv: utils.DBValues) => void) => {
  const ref = api.getDatabase().ref('Misc/env')

  ref.on(
    'value',
    (data) => {
      try {
        const val = data.val() as utils.DBValues
        emit(val)
      } catch (e) {
        const errMsg = utils.processErr(e)
        utils.log(`${createEnvConn.name}*() -> value -> ${errMsg}`)
        utils.log(e)
      }
    },
    (e) => {
      const errMsg = utils.processErr(e)
      utils.log(`${createEnvConn.name}*() -> errCb -> ${errMsg}`)
      utils.log(e)
    },
  )

  return {
    off: () => {
      ref.off()
    },
  }
}

let envConn: ReturnType<typeof createEnvConn> | null = null

function* envWatcher() {
  const authenticated = selectSessionVal('isAuth')(yield E.select())
  const signedOff = !authenticated

  if (authenticated && !envConn) {
    envConn = createEnvConn((newEnv) => {
      common.dispatch(
        mergeEnv({
          newEnv,
        }),
      )
    })
  }

  if (signedOff && envConn) {
    envConn.off()
  }
}

export function* envSaga() {
  yield E.takeEvery(setSessionVal, envWatcher)
  yield E.takeEvery(writeEnvKey, writeEnvKeyFn)
}

function* writeEnvKeyFn({
  payload: { key, value },
}: ReturnType<typeof writeEnvKey>) {
  try {
    const db = api.getDatabase()
    yield E.call(() => db.ref('Misc/env').update({ [key]: value }))
    common.dispatch(writeEnvKeyResult({ ok: true }))
  } catch (e) {
    const errMsg = utils.processErr(e)
    utils.log(`${writeEnvKeyFn.name}*() -> ${errMsg}`)
    common.dispatch(writeEnvKeyResult({ ok: false, err: errMsg }))
  }
}

//#endregion sagas
